import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { ArrowUpRight } from "lucide-react";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await register(name, email, password);
      toast.success("Account created!");
      navigate("/dashboard");
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
    setBusy(false);
  };

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:block lg:w-1/2 relative overflow-hidden bg-white/[0.04] border border-white/10">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: "radial-gradient(ellipse at 80% 30%, rgba(249,115,22,0.2), transparent 55%)" }}
          aria-hidden="true"
        />
        <img
          src="https://images.unsplash.com/photo-1667058014960-b4d3c76047a2?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-25"
        />
        <div className="relative h-full flex flex-col justify-between p-12">
          <Link to="/" className="flex items-center gap-2.5 text-white">
            <img src="/logo.png?v=3" alt="PromptsHouseBD" className="h-9 w-9 rounded-xl" />
            <span className="font-display font-bold tracking-tight">Prompts<span className="text-orange-300">HouseBD</span></span>
          </Link>
          <div>
            <h1 className="font-display text-4xl xl:text-5xl font-bold tracking-tight leading-[1.1] text-white">
              Start learning AI.<br />
              <span className="font-bn-display italic font-normal text-orange-300">Starting today.</span>
            </h1>
            <p className="text-slate-200 mt-6 max-w-md leading-relaxed">Free signup — lessons, prompts, and daily AI updates, all in one place.</p>
          </div>
          <div className="text-xs uppercase tracking-[0.2em] text-slate-200">phousebd.com · 2026</div>
        </div>
      </div>

      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-md" data-testid="register-form">
          <Link to="/" className="text-xs font-bold uppercase tracking-[0.14em] text-slate-200 hover:text-orange-300 transition-colors">← Back to home</Link>
          <div className="overline-label mt-8 mb-2">Register</div>
          <h2 className="font-display text-3xl font-bold text-white mb-8">Create your account</h2>

          {err && <div className="mb-4 border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300 rounded-lg" data-testid="register-error">{err}</div>}

          <label className="block mb-1.5 text-xs font-bold uppercase tracking-wider text-slate-200">Name</label>
          <input
            type="text" required value={name} onChange={(e) => setName(e.target.value)}
            className="input-dark mb-5"
            data-testid="register-name"
          />

          <label className="block mb-1.5 text-xs font-bold uppercase tracking-wider text-slate-200">Email</label>
          <input
            type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            className="input-dark mb-5"
            data-testid="register-email"
          />

          <label className="block mb-1.5 text-xs font-bold uppercase tracking-wider text-slate-200">Password</label>
          <input
            type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)}
            className="input-dark mb-8"
            data-testid="register-password"
          />

          <button type="submit" disabled={busy} className="btn-glow w-full justify-center" data-testid="register-submit">
            {busy ? "…" : <>Create account <ArrowUpRight size={16} /></>}
          </button>

          <div className="mt-6 text-sm text-slate-200">
            Already have an account? <Link to="/login" className="text-orange-400 font-semibold hover:underline" data-testid="register-to-login">Login</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
